import { s3Service } from "@/services/s3-service";
import { useCallback, useState } from "react";
import { FormMetadata } from "@conform-to/react";

export const usePrivateOfficeAssetsDropzone = ({
  maxFiles,
  defaultValues,
  assetType,
  form,
  fieldName,
}: {
  maxFiles?: number;
  defaultValues?: { id: string; url: string; name: string }[];
  assetType: "partner_assignment_term" | "company_assignment_term";
  form?: FormMetadata<any, any>;
  fieldName?: string;
}) => {
  const [myFiles, setMyFiles] = useState<
    { id: string; url: string; name: string }[]
  >(defaultValues ?? []);
  const [loading, setLoading] = useState<boolean>(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (maxFiles && myFiles.length + acceptedFiles.length > maxFiles) {
        return;
      }
      setLoading(true);

      const s3Promises = acceptedFiles.map(async (file) => {
        if (file.type === "application/pdf") {
          const { id, url, name } = await s3Service.uploadAssetToS3(
            file,
            assetType,
          );
          return { id, url, name };
        }
      });

      if (s3Promises.length === 0) {
        setLoading(false);
        return;
      }

      const s3PromisesResult = await Promise.all(s3Promises);

      const newFiles = [
        ...myFiles,
        ...s3PromisesResult.filter(
          (file): file is { id: string; url: string; name: string } =>
            file !== undefined,
        ),
      ];

      setMyFiles(newFiles);

      if (form && fieldName && newFiles.length > 0) {
        form.update({
          name: fieldName,
          value: newFiles[0].id,
        });
      }

      setLoading(false);
    },
    [setMyFiles, myFiles, form, fieldName],
  );

  const removeAt = useCallback(
    (index: number) => {
      setMyFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles.splice(index, 1);

        if (form && fieldName) {
          form.update({
            name: fieldName,
            value: newFiles.length > 0 ? newFiles[0].id : undefined,
          });
        }

        return newFiles;
      });
    },
    [setMyFiles, form, fieldName],
  );

  const replaceFile = useCallback(
    async (file: File) => {
      if (file.type !== "application/pdf") {
        return;
      }

      setLoading(true);

      try {
        const { id, url, name } = await s3Service.uploadAssetToS3(
          file,
          assetType,
        );

        setMyFiles([{ id, url, name }]);

        if (form && fieldName) {
          form.update({
            name: fieldName,
            value: id,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [setMyFiles, form, fieldName, assetType],
  );

  return {
    myFiles,
    setMyFiles,
    onDrop,
    loading,
    removeAt,
    replaceFile,
  };
};
